import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { PieChart as PieChartIcon, Download } from 'lucide-react';

const VALID_SOURCES = [
  "Audria Richmond®",
  "UnCloned® Free Community",
  "UnCloned® Coaching Client",
  "UnCloned® Consulting Client",
  "Instagram",
  "Facebook",
  "LinkedIn",
  "YouTube",
  "TikTok",
  "Google Search",
  "Email Newsletter",
  "Podcast",
  "Live Event or Conference",
  "Webinar or Challenge",
  "Friend or Referral",
  "Facebook Ad",
  "Instagram Ad",
  "Other",
  "Unknown / Not Provided"
];

const DATE_RANGES = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'All Time', value: 'all' }
];

const COLORS = [
  '#FF7901', '#df1283', '#22C55E', '#3B82F6', '#8B5CF6', 
  '#F59E0B', '#EC4899', '#06B6D4', '#84CC16', '#F97316',
  '#6366F1', '#14B8A6', '#EF4444', '#A855F7', '#0EA5E9'
];

const TrafficSourcesPieChart = ({ data, onDateChange, selectedDateRange }) => {
  const chartRef = React.useRef(null);
  
  const downloadPNG = async () => {
    if (!chartData || chartData.length === 0) return;
    
    try {
      // Instagram portrait size
      const WIDTH = 1080;
      const HEIGHT = 1350;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
      
      // Black background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Load and draw logo at top
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      
      logoImg.onload = () => {
        // Logo - sized and positioned closer to chart
        const logoWidth = 420;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        const logoX = (WIDTH - logoWidth) / 2;
        const logoY = 40;
        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
        
        // Chart positioning - closer to logo
        const logoBottom = logoY + logoHeight;
        const chartAreaTop = logoBottom + 30;
        const chartAreaBottom = HEIGHT - 220; // Room for legend
        const chartAreaHeight = chartAreaBottom - chartAreaTop;
        
        const centerX = WIDTH / 2;
        const centerY = chartAreaTop + (chartAreaHeight / 2);
        const outerRadius = Math.min(chartAreaHeight / 2 - 20, 380);
        const innerRadius = outerRadius * 0.4;
        
        const total = chartData.reduce((sum, item) => sum + item.value, 0);
        let currentAngle = -Math.PI / 2;
        
        chartData.forEach((item) => {
          const sliceAngle = (item.value / total) * 2 * Math.PI;
          
          ctx.beginPath();
          ctx.moveTo(centerX + innerRadius * Math.cos(currentAngle), centerY + innerRadius * Math.sin(currentAngle));
          ctx.arc(centerX, centerY, outerRadius, currentAngle, currentAngle + sliceAngle);
          ctx.arc(centerX, centerY, innerRadius, currentAngle + sliceAngle, currentAngle, true);
          ctx.closePath();
          ctx.fillStyle = item.color;
          ctx.fill();
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 3;
          ctx.stroke();
          
          const percentage = Math.round((item.value / total) * 100);
          if (percentage >= 5) {
            const labelAngle = currentAngle + sliceAngle / 2;
            const labelRadius = innerRadius + (outerRadius - innerRadius) * 0.55;
            const labelX = centerX + labelRadius * Math.cos(labelAngle);
            const labelY = centerY + labelRadius * Math.sin(labelAngle);
            
            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${percentage}%`, labelX, labelY);
          }
          
          currentAngle += sliceAngle;
        });
        
        // Draw legend at bottom with 3 columns
        const legendStartY = HEIGHT - 180;
        const cols = 3;
        const legendItemWidth = 340;
        const totalLegendWidth = cols * legendItemWidth;
        const startX = (WIDTH - totalLegendWidth) / 2 + legendItemWidth / 2;
        
        chartData.slice(0, 9).forEach((item, index) => {
          const col = index % cols;
          const row = Math.floor(index / cols);
          const x = startX + col * legendItemWidth;
          const y = legendStartY + row * 50;
          
          // Color dot
          ctx.beginPath();
          ctx.arc(x - 130, y, 10, 0, Math.PI * 2);
          ctx.fillStyle = item.color;
          ctx.fill();
          
          // Label text
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.font = '15px system-ui, -apple-system, sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          const labelText = item.name.length > 18 ? item.name.substring(0, 16) + '...' : item.name;
          ctx.fillText(labelText, x - 110, y);
          
          // Count number
          ctx.fillStyle = '#FF7901';
          ctx.font = 'bold 16px system-ui, -apple-system, sans-serif';
          ctx.textAlign = 'right';
          ctx.fillText(item.value.toString(), x + 130, y);
        });
        
        const pngUrl = canvas.toDataURL('image/png', 1.0);
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `traffic-sources-${selectedDateRange || 'all'}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };
      
      logoImg.onerror = () => {
        // If logo fails to load, draw chart without it
        const centerX = WIDTH / 2;
        const centerY = HEIGHT / 2 - 80;
        const outerRadius = 380;
        const innerRadius = outerRadius * 0.4;
        
        const total = chartData.reduce((sum, item) => sum + item.value, 0);
        let currentAngle = -Math.PI / 2;
        
        chartData.forEach((item) => {
          const sliceAngle = (item.value / total) * 2 * Math.PI;
          ctx.beginPath();
          ctx.moveTo(centerX + innerRadius * Math.cos(currentAngle), centerY + innerRadius * Math.sin(currentAngle));
          ctx.arc(centerX, centerY, outerRadius, currentAngle, currentAngle + sliceAngle);
          ctx.arc(centerX, centerY, innerRadius, currentAngle + sliceAngle, currentAngle, true);
          ctx.closePath();
          ctx.fillStyle = item.color;
          ctx.fill();
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 3;
          ctx.stroke();
          
          const percentage = Math.round((item.value / total) * 100);
          if (percentage >= 5) {
            const labelAngle = currentAngle + sliceAngle / 2;
            const labelRadius = innerRadius + (outerRadius - innerRadius) * 0.55;
            const labelX = centerX + labelRadius * Math.cos(labelAngle);
            const labelY = centerY + labelRadius * Math.sin(labelAngle);
            ctx.fillStyle = '#FFFFFF';
            ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${percentage}%`, labelX, labelY);
          }
          currentAngle += sliceAngle;
        });
        
        // Draw legend with 3 columns
        const legendStartY = HEIGHT - 180;
        const cols = 3;
        const legendItemWidth = 340;
        const totalLegendWidth = cols * legendItemWidth;
        const startX = (WIDTH - totalLegendWidth) / 2 + legendItemWidth / 2;
        
        chartData.slice(0, 9).forEach((item, index) => {
          const col = index % cols;
          const row = Math.floor(index / cols);
          const x = startX + col * legendItemWidth;
          const y = legendStartY + row * 50;
          
          ctx.beginPath();
          ctx.arc(x - 130, y, 10, 0, Math.PI * 2);
          ctx.fillStyle = item.color;
          ctx.fill();
          
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.font = '15px system-ui, -apple-system, sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          const labelText = item.name.length > 18 ? item.name.substring(0, 16) + '...' : item.name;
          ctx.fillText(labelText, x - 110, y);
          
          ctx.fillStyle = '#FF7901';
          ctx.font = 'bold 16px system-ui, -apple-system, sans-serif';
          ctx.textAlign = 'right';
          ctx.fillText(item.value.toString(), x + 130, y);
        });
        
        const pngUrl = canvas.toDataURL('image/png', 1.0);
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `traffic-sources-${selectedDateRange || 'all'}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };
      
      logoImg.src = '/new-logo-homepage.png';
    } catch (err) {
      console.error('Error downloading PNG:', err);
    }
  };
  
  if (!data || !data.referrals || data.referrals.length === 0) {
    return (
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-[#FF7901]" />
            Traffic Sources
          </h3>
          <select 
            value={selectedDateRange || 'all'}
            onChange={(e) => onDateChange(e.target.value)}
            className="bg-[#4A0A66]/50 text-white text-xs px-3 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#FF7901]"
          >
            {DATE_RANGES.map(range => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
        </div>
        <p className="text-sm text-[#FDF2F8]/50 text-center py-6">No traffic data for this period</p>
      </div>
    );
  }
  
  const filteredReferrals = data.referrals.filter(r => VALID_SOURCES.includes(r.source));
  
  const chartData = filteredReferrals.slice(0, 10).map((item, index) => ({
    name: item.source,
    value: item.count,
    percentage: item.percentage,
    color: COLORS[index % COLORS.length]
  }));
  
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium text-sm">{data.name}</p>
          <p className="text-[#FF7901] text-sm">{data.value} users ({data.percentage}%)</p>
        </div>
      );
    }
    return null;
  };
  
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle" 
        dominantBaseline="central"
        className="text-xs font-bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
  
  return (
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <PieChartIcon className="w-5 h-5 text-[#FF7901]" />
          Traffic Sources
        </h3>
        <div className="flex items-center gap-2">
          <select 
            value={selectedDateRange || 'all'}
            onChange={(e) => onDateChange(e.target.value)}
            className="bg-[#4A0A66]/50 text-white text-xs px-3 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#FF7901] cursor-pointer"
          >
            {DATE_RANGES.map(range => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
          <button
            onClick={downloadPNG}
            className="bg-[#FF7901] hover:bg-[#FF7901]/80 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
            title="Download PNG"
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>
      </div>
      
      <div className="text-center text-sm text-[#FDF2F8]/60 mb-2">
        {data.total_users || filteredReferrals.reduce((sum, r) => sum + r.count, 0)} users
      </div>
      
      <div className="h-72 lg:h-80" ref={chartRef}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius="80%"
              innerRadius="35%"
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="#1a0527" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legend */}
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {chartData.slice(0, 8).map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div 
              className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs text-[#FDF2F8]/70 truncate">{item.name}</span>
            <span className="text-xs text-[#FF7901] ml-auto">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrafficSourcesPieChart;
